import React from 'react';
import { observer } from 'mobx-react';

import CustomFieldDisplayValue from 'components/SharedFormComponents/CustomFieldDisplayValue';
import AuditScoreChip from 'components/Audits/AuditScoreChip';
import { AuditQuestionDrawerTab } from 'components/Audits/AuditQuestionDrawer';
import { lookupAnswerEntries, lookupEntryUuids } from 'components/Audits/lookupRecords';
import { QuestionRowButtonKind, ROW_BUTTONS, rowButtonLabel } from 'components/Audits/questionDrawer';

import { EMPTY_STATS } from 'stores/AuditQuestionActivities';

import { ProtectorType } from 'core/constants';

import { AnswerTypes } from 'utils/api/modules/incidents/incidents.types';
import {
  AuditQuestionScoreState,
  AuditScoreQuestion,
  EnumOptions,
  FormBuilderProperties,
  ImageType,
} from 'utils/api/modules/entities/entities.types';

import AuditRecordStore from 'stores/AuditRecord';

import { ActionButton, ActionButtons, CountBadge } from 'components/Audits/styles';

import {
  AnswerRow,
  AnswerWrapper,
  HeaderActions,
  Mandatory,
  NotAnswered,
  NotApplicable,
  QuestionCard,
  QuestionHeader,
  QuestionLabel,
} from './styles';
import AuditAttachmentAnswer from './AuditAttachmentAnswer';
import AuditQuestionImage from './AuditQuestionImage';
import { AuditAttachment } from './types';

// The shared config's `kind` is not the testid: the E2E phase is written against these names.
const BUTTON_TEST_IDS: Record<QuestionRowButtonKind, string> = {
  tasks: 'audit-question-actions-button',
  activity: 'audit-question-activity-button',
  links: 'audit-question-relations-button',
};

export type AuditQuestionRowProps = {
  store: AuditRecordStore;
  questionKey: string;
  question: FormBuilderProperties;
  mandatory: boolean;
  score?: AuditScoreQuestion;
  /** The question whose drawer is open reads back on the record, so the two surfaces agree. */
  open?: boolean;
  onOpenDrawer: (questionKey: string, tab: AuditQuestionDrawerTab) => void;
};

const AuditQuestionRow: React.FunctionComponent<AuditQuestionRowProps> = ({
  store,
  questionKey,
  question,
  mandatory,
  score,
  open,
  onOpenDrawer,
}): React.ReactElement | null => {
  const details = store.details;

  if (!details?.submission || !question) {
    return null;
  }

  // The stored option keys, not mapped labels: the chip resolves label and colour from the options itself.
  const value = details.submission.content[questionKey];

  // Said in words rather than a dash: on a submitted audit a blank is a finding, not a placeholder.
  const answered = !(
    value === null ||
    value === undefined ||
    value === '' ||
    (Array.isArray(value) && value.length === 0)
  );

  const stats = store.questionActivities?.statsFor(questionKey) || EMPTY_STATS;
  const { description, questionType, properties } = question;
  // Double hop needed: `properties.lookupType` types as FormBuilderProperties, not a string.
  const lookupType = properties?.lookupType as unknown as string | undefined;
  const answerRecords = lookupEntryUuids(lookupAnswerEntries(questionType, value)).length;
  // `file` is null when the image is gone (soft-deleted), so gate on the URL rather than on `image`.
  const imageFile = (properties?.image as unknown as ImageType | undefined)?.file;
  const imageUrl = typeof imageFile === 'string' ? imageFile : undefined;
  // The record serialises an ATTACHMENT answer as [{ filename, size, url }].
  const attachments =
    questionType === ProtectorType.Attachment && Array.isArray(value) ? (value as unknown as AuditAttachment[]) : null;

  return (
    <QuestionCard $open={open} data-testid={`audit-question-${questionKey}`} data-open={open || undefined}>
      <QuestionHeader>
        <QuestionLabel>
          {description || '-'}
          {mandatory && <Mandatory aria-hidden="true">*</Mandatory>}
        </QuestionLabel>

        <HeaderActions>
          {/* PRD §6: `excluded` is out of the denominator and shows a muted marker rather than a
              chip; `unscored` carries no scores at all and shows nothing. */}
          {score?.state === AuditQuestionScoreState.Excluded ? (
            <NotApplicable data-testid={`audit-question-na-marker-${questionKey}`}>N/A</NotApplicable>
          ) : (
            <AuditScoreChip
              percentage={score?.percentage}
              label={
                score && score.earned !== null && score.max !== null ? `${score.earned} / ${score.max}` : undefined
              }
              data-testid={`audit-question-score-${questionKey}`}
            />
          )}

          <ActionButtons>
            {ROW_BUTTONS.map((button) => {
              const count = button.count(stats, answerRecords);

              return (
                <ActionButton
                  key={button.kind}
                  type="button"
                  data-testid={BUTTON_TEST_IDS[button.kind]}
                  data-badge-kind={button.kind}
                  aria-label={rowButtonLabel(button.label, count)}
                  onClick={() => onOpenDrawer(questionKey, button.tab)}
                >
                  <button.Icon />
                  {button.label}
                  {count > 0 && <CountBadge>{count}</CountBadge>}
                </ActionButton>
              );
            })}
          </ActionButtons>
        </HeaderActions>
      </QuestionHeader>

      {imageUrl && (
        <AuditQuestionImage questionKey={questionKey} url={imageUrl} label={description || 'Question image'} />
      )}

      <AnswerRow>
        <AnswerWrapper $withIcon={questionType === ProtectorType.Location}>
          {answered ? (
            attachments ? (
              <AuditAttachmentAnswer questionKey={questionKey} files={attachments} />
            ) : (
              <CustomFieldDisplayValue
                type={questionType as ProtectorType}
                value={value as AnswerTypes}
                lookupType={lookupType}
                submissionTab
                choiceChips
                choiceOptions={properties?.enum as unknown as EnumOptions}
              />
            )
          ) : (
            <NotAnswered data-testid={`audit-question-not-answered-${questionKey}`}>Not answered</NotAnswered>
          )}
        </AnswerWrapper>
      </AnswerRow>
    </QuestionCard>
  );
};

export default observer(AuditQuestionRow);
